/**
 * compras-store — Módulo de Compras (Round D / D1).
 *
 * Hooks TanStack Query pro `/api/v1/compras` (gated por COMPRAS_ENABLED +
 * requireProfile compras/admin no backend). Catálogo de tipos de item +
 * cadastro de fornecedores (+faturamento direto) + alíquota de NF por empresa.
 * Dinheiro em CENTAVOS. ZERO localStorage.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { api, hasToken } from './api';
import { QUADRO_KEY } from './solicitacoes-compra-store';

export interface CompraTipoItem {
  id: string;
  nome: string;
  ativo: boolean;
  ordem: number;
  criadoEmIso: string;
  atualizadoEmIso: string;
}

export interface FaturamentoDireto {
  fazManutencao: boolean;
  minFaturarVendaCentavos: number;
  minFaturarManutencaoCentavos: number;
  impostoPct: number;
  cobraImpostoRetirada: boolean;
  pedeNfRetirada: boolean;
  pedeNfAbatimento: boolean;
}

/** Fase 2: parente = outro cadastro com a MESMA RAIZ de CNPJ (matriz/filial). Informativo. */
export interface FornecedorParente {
  id: string;
  nome: string;
  /** '0001' = matriz; '0002'… = filial. Vem do CNPJ, ninguém digita. */
  ordemEstabelecimento: string;
  /** A cidade do cadastro (ou "CNPJ final 0006-99") — separa filiais de nome igual. */
  lugar?: string | null;
}

export interface Fornecedor {
  id: string;
  /** Fantasia quando houver, senão a razão social. */
  nome: string;
  /** Fase 2: a tela mostra os DOIS — duas empresas diferentes usam a marca "INNOVATIE". */
  razaoSocial: string;
  nomeFantasia: string | null;
  /** Os 8 primeiros dígitos do CNPJ. null = sem CNPJ válido → a tela não mostra selo nenhum. */
  raizCnpj: string | null;
  parentes: FornecedorParente[];
  /** 17/09: a cidade ao lado do nome, quando há parentes (ou o final do CNPJ). */
  lugar?: string | null;
  cnpj: string | null;
  telefone: string | null;
  email: string | null;
  observacoes: string | null;
  ativo: boolean;
  faturamento: FaturamentoDireto;
  tipoItemIds: string[];
  criadoEmIso: string;
  atualizadoEmIso: string;
}

export interface EmpresaCompras {
  id: string;
  cnpj: string;
  razaoSocial: string;
  nomeFantasia: string | null;
  /** FX-5: apelido editável pelo admin (rótulo da UI). null = usa razão + CNPJ. */
  apelido: string | null;
  ativo: boolean;
  /** Item 3 (mig 554): com a de serviço ao lado, esta é a alíquota de PRODUTO. */
  aliquotaNfPct: number;
  /** Item 3 (mig 554): alíquota de NF de serviço. null = não configurada → usa a de produto. */
  aliquotaNfServicoPct: number | null;
  criadoEmIso: string;
}

/**
 * FX-5: rótulo que DIFERENCIA as 2 empresas IGM (nome de fantasia igual). Usa o
 * apelido quando houver; senão razão social + CNPJ. Opcionalmente inclui a
 * alíquota de NF (útil no select de empresa de faturamento).
 */
export function empresaLabel(
  emp: { apelido?: string | null; razaoSocial: string; cnpj: string; aliquotaNfPct?: number },
  opts: { comAliquota?: boolean } = {}
): string {
  const base = emp.apelido?.trim() || `${emp.razaoSocial} · ${emp.cnpj}`;
  return opts.comAliquota && typeof emp.aliquotaNfPct === 'number'
    ? `${base} (${emp.aliquotaNfPct}% NF)`
    : base;
}

export interface CreateFornecedorInput {
  nome: string;
  cnpj?: string | null;
  telefone?: string | null;
  email?: string | null;
  observacoes?: string | null;
  faturamento?: Partial<FaturamentoDireto>;
  tipoItemIds?: string[];
}
export type UpdateFornecedorInput = Partial<CreateFornecedorInput> & { ativo?: boolean };

const KEY = ['compras'] as const;

export function useTiposItem() {
  return useQuery({
    queryKey: [...KEY, 'tipos'] as const,
    queryFn: () => api.get<{ items: CompraTipoItem[] }>('/api/v1/compras/tipos-item'),
    enabled: hasToken(),
  });
}

/** FX-8: catálogo de Setores (uso interno). */
export interface CompraSetor {
  id: string;
  nome: string;
  ativo: boolean;
  ordem: number;
  criadoEmIso: string;
  atualizadoEmIso: string;
}
export function useSetores() {
  return useQuery({
    queryKey: [...KEY, 'setores'] as const,
    queryFn: () => api.get<{ items: CompraSetor[] }>('/api/v1/compras/setores'),
    enabled: hasToken(),
  });
}

/** FX-16: catálogo de Origens do item da cotação. */
export type OrigemModo = 'custo_venda' | 'venda_custo';
export interface CompraOrigem {
  id: string;
  nome: string;
  modo: OrigemModo;
  fator: number;
  ativo: boolean;
  ordem: number;
  criadoEmIso: string;
  atualizadoEmIso: string;
}
export function useOrigens() {
  return useQuery({
    queryKey: [...KEY, 'origens'] as const,
    queryFn: () => api.get<{ items: CompraOrigem[] }>('/api/v1/compras/origens'),
    enabled: hasToken(),
  });
}

/** FX-24: catálogo de Formas de pagamento. */
export interface CompraFormaPagamento {
  id: string;
  nome: string;
  ativo: boolean;
  ordem: number;
  /** FX-28: esta forma debita o saldo de crédito do fornecedor. */
  consomeCredito: boolean;
  criadoEmIso: string;
  atualizadoEmIso: string;
}
export function useFormasPagamento() {
  return useQuery({
    queryKey: [...KEY, 'formas-pagamento'] as const,
    queryFn: () => api.get<{ items: CompraFormaPagamento[] }>('/api/v1/compras/formas-pagamento'),
    enabled: hasToken(),
  });
}

/** FX-21: config de destinatários das notificações/e-mail do Compras. */
export interface CompraNotifMembro {
  userId: string;
  nome: string;
  email: string;
  recebeNotif: boolean;
  recebeEmailDiario: boolean;
}
export function useComprasNotifConfig() {
  return useQuery({
    queryKey: [...KEY, 'notif-config'] as const,
    queryFn: () => api.get<{ items: CompraNotifMembro[] }>('/api/v1/compras/notif-config'),
    enabled: hasToken(),
  });
}
export function useSetComprasNotifConfig() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      userId,
      patch,
    }: {
      userId: string;
      patch: { recebeNotif?: boolean; recebeEmailDiario?: boolean };
    }) => api.put<CompraNotifMembro>(`/api/v1/compras/notif-config/${userId}`, patch),
    onSuccess: () => qc.invalidateQueries({ queryKey: [...KEY, 'notif-config'] }),
  });
}

export function useFornecedores() {
  return useQuery({
    queryKey: [...KEY, 'fornecedores'] as const,
    queryFn: () => api.get<{ items: Fornecedor[] }>('/api/v1/compras/fornecedores'),
    enabled: hasToken(),
  });
}

export function useEmpresasCompras() {
  return useQuery({
    queryKey: [...KEY, 'empresas'] as const,
    queryFn: () => api.get<{ items: EmpresaCompras[] }>('/api/v1/compras/empresas'),
    enabled: hasToken(),
  });
}

export function useComprasActions() {
  const qc = useQueryClient();
  const invTipos = () => qc.invalidateQueries({ queryKey: [...KEY, 'tipos'] });
  const invForn = () => qc.invalidateQueries({ queryKey: [...KEY, 'fornecedores'] });
  const invEmp = () => qc.invalidateQueries({ queryKey: [...KEY, 'empresas'] });
  const invSetores = () => qc.invalidateQueries({ queryKey: [...KEY, 'setores'] });
  const invFormasPag = () => qc.invalidateQueries({ queryKey: [...KEY, 'formas-pagamento'] });
  const invOrigens = () => qc.invalidateQueries({ queryKey: [...KEY, 'origens'] });
  // CO-4: a sugestão de fornecedores por tipo na cotação (`forn-por-tipo`)
  // fica stale quando os tipos de um fornecedor mudam. Invalida tudo (qualquer
  // tipoItemId) — partial key match.
  const invFornPorTipo = () => qc.invalidateQueries({ queryKey: [...KEY, 'forn-por-tipo'] });
  const invFornTudo = () => {
    invForn();
    invFornPorTipo();
  };

  const createTipo = useMutation({
    mutationFn: (input: { nome: string }) =>
      api.post<CompraTipoItem>('/api/v1/compras/tipos-item', input),
    onSuccess: invTipos,
  });
  const updateTipo = useMutation({
    mutationFn: ({ id, patch }: { id: string; patch: { nome?: string; ativo?: boolean } }) =>
      api.patch<CompraTipoItem>(`/api/v1/compras/tipos-item/${id}`, patch),
    onSuccess: invTipos,
  });
  const deleteTipo = useMutation({
    mutationFn: (id: string) => api.delete<{ ok: true }>(`/api/v1/compras/tipos-item/${id}`),
    // apagar tipo cascateia o M2M → fornecedores (e a sugestão por tipo) também mudam.
    onSuccess: () => {
      invTipos();
      invFornTudo();
    },
  });

  const createFornecedor = useMutation({
    mutationFn: (input: CreateFornecedorInput) =>
      api.post<Fornecedor>('/api/v1/compras/fornecedores', input),
    onSuccess: invFornTudo,
  });
  const updateFornecedor = useMutation({
    mutationFn: ({ id, patch }: { id: string; patch: UpdateFornecedorInput }) =>
      api.patch<Fornecedor>(`/api/v1/compras/fornecedores/${id}`, patch),
    onSuccess: invFornTudo,
  });
  const deleteFornecedor = useMutation({
    mutationFn: (id: string) => api.delete<{ ok: true }>(`/api/v1/compras/fornecedores/${id}`),
    onSuccess: invFornTudo,
  });

  const setAliquota = useMutation({
    mutationFn: ({ id, aliquotaNfPct }: { id: string; aliquotaNfPct: number }) =>
      api.put<EmpresaCompras>(`/api/v1/compras/empresas/${id}/aliquota`, { aliquotaNfPct }),
    onSuccess: invEmp,
  });

  // Item 3 (mig 554): a alíquota de NF de SERVIÇO — mesmo caminho, campo ao lado.
  const setAliquotaServico = useMutation({
    mutationFn: ({
      id,
      aliquotaNfServicoPct,
    }: {
      id: string;
      aliquotaNfServicoPct: number | null;
    }) =>
      api.put<EmpresaCompras>(`/api/v1/compras/empresas/${id}/aliquota-servico`, {
        aliquotaNfServicoPct,
      }),
    onSuccess: invEmp,
  });

  // FX-5: apelido editável (rótulo curto que diferencia as 2 IGM). null = limpa.
  const setApelido = useMutation({
    mutationFn: ({ id, apelido }: { id: string; apelido: string | null }) =>
      api.put<EmpresaCompras>(`/api/v1/compras/empresas/${id}/apelido`, { apelido }),
    onSuccess: invEmp,
  });

  // FX-8: catálogo de Setores (uso interno).
  const createSetor = useMutation({
    mutationFn: (input: { nome: string }) =>
      api.post<CompraSetor>('/api/v1/compras/setores', input),
    onSuccess: invSetores,
  });
  const updateSetor = useMutation({
    mutationFn: ({ id, patch }: { id: string; patch: { nome?: string; ativo?: boolean } }) =>
      api.patch<CompraSetor>(`/api/v1/compras/setores/${id}`, patch),
    onSuccess: invSetores,
  });
  const deleteSetor = useMutation({
    mutationFn: (id: string) => api.delete<{ ok: true }>(`/api/v1/compras/setores/${id}`),
    onSuccess: invSetores,
  });

  // FX-24: catálogo de Formas de pagamento.
  const createFormaPagamento = useMutation({
    mutationFn: (input: { nome: string }) =>
      api.post<CompraFormaPagamento>('/api/v1/compras/formas-pagamento', input),
    onSuccess: invFormasPag,
  });
  const updateFormaPagamento = useMutation({
    mutationFn: ({ id, patch }: { id: string; patch: { nome?: string; ativo?: boolean } }) =>
      api.patch<CompraFormaPagamento>(`/api/v1/compras/formas-pagamento/${id}`, patch),
    onSuccess: invFormasPag,
  });
  const deleteFormaPagamento = useMutation({
    mutationFn: (id: string) => api.delete<{ ok: true }>(`/api/v1/compras/formas-pagamento/${id}`),
    onSuccess: invFormasPag,
  });

  // FX-16: catálogo de Origens. Mudança de fator/modo afeta o cálculo das
  // cotações abertas → invalida também as cotações.
  const invOrigemEffects = () => {
    invOrigens();
    qc.invalidateQueries({ queryKey: [...KEY, 'cotacao'] });
  };
  const createOrigem = useMutation({
    mutationFn: (input: { nome: string; modo?: OrigemModo; fator?: number }) =>
      api.post<CompraOrigem>('/api/v1/compras/origens', input),
    onSuccess: invOrigens,
  });
  const updateOrigem = useMutation({
    mutationFn: ({
      id,
      patch,
    }: {
      id: string;
      patch: { nome?: string; modo?: OrigemModo; fator?: number; ativo?: boolean };
    }) => api.patch<CompraOrigem>(`/api/v1/compras/origens/${id}`, patch),
    onSuccess: invOrigemEffects,
  });
  const deleteOrigem = useMutation({
    mutationFn: (id: string) => api.delete<{ ok: true }>(`/api/v1/compras/origens/${id}`),
    onSuccess: invOrigens,
  });

  return {
    createTipo,
    updateTipo,
    deleteTipo,
    createFornecedor,
    updateFornecedor,
    deleteFornecedor,
    setAliquota,
    setAliquotaServico,
    setApelido,
    createSetor,
    updateSetor,
    deleteSetor,
    createFormaPagamento,
    updateFormaPagamento,
    deleteFormaPagamento,
    createOrigem,
    updateOrigem,
    deleteOrigem,
  };
}

// ─── D2: cotação multi-fornecedor ───────────────────────────────────────
export type CotacaoStatus = 'aberta' | 'fechada';
// FX-16: origem virou catálogo (`compra_origem`); o id costuma ser
// 'comercial'/'manutencao' (seed) mas pode ser qualquer origem cadastrada.
export type CotacaoOrigem = string;

export interface CotacaoListItem {
  id: string;
  titulo: string;
  status: CotacaoStatus;
  /** FX-20: número sequencial humano (#N) da cotação. */
  numeroHumano: number | null;
  criadoPor: string | null;
  criadoEmIso: string;
  atualizadoEmIso: string;
}
export interface BlocoComputed {
  id: string;
  fornecedorId: string;
  cotacaoUnitCentavos: number;
  descontoPct: number;
  totalCentavos: number;
  margemSemDescontoCentavos: number;
  totalComDescontoCentavos: number;
  margemComDescontoCentavos: number;
  /** FX-36: nº do pedido/NF por fornecedor (obrigatório na efetivação). */
  numeroPedidoNf: string | null;
}
/** D5b — categorias do custo realizado (espelha projeto_custos). */
export type RealizadoCategoria =
  | 'material'
  | 'mao_obra_terceira'
  | 'mao_obra_propria'
  | 'logistica'
  | 'outros';
export type RealizadoClassificacao = 'previsto' | 'imprevisto';

/** D4: impostos do faturamento direto (sobre a margem). null quando OFF. */
export interface FaturamentoComputed {
  /** Fase 2: o % do distribuidor (`forn_imposto_pct`) está preenchido no cadastro? false → os nulls abaixo. */
  impostoDistribuidorPreenchido: boolean;
  /** margem × % do fornecedor — a NF DELE. Entra sempre. */
  impostoDistribuidorCentavos: number | null;
  /** O que a IGM tem hoje, com o crédito RETIDO no fornecedor. */
  margemComCreditoRetidoCentavos: number | null;
  /** O fornecedor cobra NF de serviço para a IGM retirar o crédito? */
  cobraNaRetirada: boolean;
  impostoServicoRetiradaCentavos: number | null;
  /** "Se retirar, sobram" — já com a NF de serviço descontada. */
  margemSeRetirarCentavos: number | null;
  /** A alíquota de SERVIÇO da empresa, que entrou na conta da retirada (%). null = não configurada. */
  aliquotaRetiradaPct: number | null;
}
export interface CotacaoItemFull {
  id: string;
  cotacaoId: string;
  descricao: string;
  tipoItemId: string | null;
  origem: CotacaoOrigem;
  quantidade: number;
  vendaTotalCentavos: number;
  fornecedorEscolhidoId: string | null;
  valorCompradoCentavos: number | null;
  faturamentoDireto: boolean;
  empresaFaturamentoId: string | null;
  // FX-24 — forma de pagamento escolhida (catálogo). null = não informada.
  formaPagamentoId: string | null;
  // FX-28 — gera crédito no fornecedor (marcador do fat. direto, caso a caso).
  geraCredito: boolean;
  // D5b — elo com a solicitação + dados do lançamento no realizado.
  solicitacaoItemId: string | null;
  realizadoCategoria: RealizadoCategoria | null;
  realizadoClassificacao: RealizadoClassificacao | null;
  realizadoCustoId: string | null;
  // D6 — logística.
  previsaoChegada: string | null;
  /** SU-1 — chegou na empresa (etapa "recebido" do status canônico). */
  recebido: boolean;
  entregue: boolean;
  // CO-6 — estado da aprovação do Financeiro (null = direto, flag OFF).
  /** FX-35: 'aprovado' = liberada (a efetivar). Fase 2: 'pendente'/'recusado' morreram com a alçada. */
  realizadoAprovacaoStatus: 'aprovado' | null;
  realizadoAprovadoPor: string | null;
  realizadoAprovadoEmIso: string | null;
  realizadoMotivoRecusa: string | null;
  /** FX-35: compra efetivada (Aprovado → Comprado). */
  compraEfetivada: boolean;
  ordem: number;
  blocos: BlocoComputed[];
  melhorMargemIdx: number;
  /** D3: valor comprado efetivo (auto do escolhido ou manual) + margem final. null = não fechado. */
  /** O FECHADO DA COMPRA (só a parte comprada). O custo do item inteiro é `custoTotalCentavos`. */
  valorCompradoEfetivoCentavos: number | null;
  /** 17/09 — a divisão: quantas saem do estoque, quantas se compram, e o custo de cada parte. */
  qtdEstoque: number;
  qtdComprar: number;
  custoEstoqueUnitCentavos: number | null;
  custoEstoqueCentavos: number | null;
  custoTotalCentavos: number | null;
  /** Régua do #311: por que o custo/a margem ainda não existem. null = existem. */
  fechadoFalta: string | null;
  margemFalta: string | null;
  /** A venda vem do Ganho (não se edita aqui). false = item fora da venda. */
  vendaDoGanho: boolean;
  margemFinalCentavos: number | null;
  margemFinalPct: number | null;
  /** D4: a venda do item atinge o mínimo do fornecedor escolhido E o mínimo está preenchido (#326). */
  faturamentoDiretoElegivel: boolean;
  /** #326: o motivo — a tela escreve o aviso certo. null = sem fornecedor escolhido. */
  faturamentoDiretoMotivo: 'elegivel' | 'abaixo-do-minimo' | 'minimo-nao-preenchido' | null;
  /** O mínimo que vale para o item — o aviso escreve os dois valores. null = não preenchido. */
  faturamentoDiretoMinimoCentavos: number | null;
  /** #326 (mig 619): ligado antes da regra do mínimo — continua como está, e a tela diz. */
  fatDiretoAntesDaRegraDoMinimo: boolean;
  /** D4: impostos + margem líquida. null quando faturamento direto OFF. */
  faturamento: FaturamentoComputed | null;
  // ── Fase 2 de Compras (15/09/2026, mig 615) ──
  /** De onde o material sai. 'cotacao' = fornecedor cotado (padrão); os outros PULAM a cotação. */
  ondeComprou: OndeComprou;
  ondeComprouNome: string | null;
  /** O que veio na nota. null = a nota ainda não chegou. O FECHADO é `valorCompradoEfetivoCentavos`. */
  custoRecebidoCentavos: number | null;
  fornecedorEscolhidoPor: string | null;
  fornecedorEscolhidoEmIso: string | null;
  fornecedorEscolhidoPorNome: string | null;
  /** A ponte com o levantamento. null = item avulso. */
  projetoItemId: string | null;
  projetoId: string | null;
  codigoProduto: string | null;
  codigo: string | null;
  /** O que foi VENDIDO desse item (congelado no Ganho). null = item novo no levantamento. */
  quantidadeVendida: number | null;
  vendaUnitCentavos: number | null;
  /** Índice (em `blocos`) do mais barato (menor preço cotado antes do desconto). -1 = nenhum. */
  maisBaratoIdx: number;
  /** As duas pontas (mais barato × melhor margem) são fornecedores diferentes. */
  pontasDivergem: boolean;
  ganhoNegociacaoCentavos: number | null;
  economiaPct: number | null;
  /** Recebido − fechado. null enquanto a nota não chegou. */
  diferencaRecebidoCentavos: number | null;
  /**
   * 🔴 18/09 (o buraco): o aviso do levantamento para este item — a quantidade mudou (`para` = o que o Compras aplica com um clique),
   * o item saiu do levantamento, comprou-se a mais. null = nada a avisar.
   */
  avisoLevantamento?: {
    tipo: 'ajustar' | 'sobra' | 'retirado' | 'aceita';
    frase: string;
    para: number | null;
    /** 🔴 #367: a quantidade que o Compras pode MANTER (aceitar a divergência, com motivo). null = nada a aceitar. */
    manter?: number | null;
    /** 🔴 #367: a divergência já aceita (tipo 'aceita'). */
    aceitacao?: { motivo: string; por: string | null; emIso: string } | null;
  } | null;
  /** 🔴 #377: a linha é de uma solicitação CANCELADA (a frase vem pronta do servidor). null = viva. */
  solicitacaoCancelada?: { numero: number | null; porNome: string | null; emIso: string | null; motivo: string | null; frase: string } | null;
}
/** Fase 2: "Fornecedor é de quem se COTA. Varejo e marketplace são ONDE se compra." */
export type OndeComprou = 'cotacao' | 'estoque' | 'marketplace' | 'varejo';
export const ONDE_COMPROU_LABEL: Record<OndeComprou, string> = {
  cotacao: 'Cotação com fornecedor',
  estoque: 'Estoque IGM',
  marketplace: 'Marketplace (Mercado Livre…)',
  varejo: 'Loja de varejo',
};
export interface CotacaoFull {
  cotacao: CotacaoListItem;
  itens: CotacaoItemFull[];
}

export interface UpdateCotacaoItemInput {
  descricao?: string;
  tipoItemId?: string | null;
  origem?: CotacaoOrigem;
  quantidade?: number;
  vendaTotalCentavos?: number;
  fornecedorEscolhidoId?: string | null;
  valorCompradoCentavos?: number | null;
  faturamentoDireto?: boolean;
  empresaFaturamentoId?: string | null;
  formaPagamentoId?: string | null;
  geraCredito?: boolean;
  solicitacaoItemId?: string | null;
  realizadoCategoria?: RealizadoCategoria | null;
  realizadoClassificacao?: RealizadoClassificacao | null;
  previsaoChegada?: string | null;
  /** SU-1 — chegou na empresa. */
  recebido?: boolean;
  entregue?: boolean;
  // ── Fase 2 de Compras ──
  ondeComprou?: OndeComprou;
  ondeComprouNome?: string | null;
  custoRecebidoCentavos?: number | null;
  // ── 17/09: a divisão (quantas do estoque e quanto custa cada uma) ──
  qtdEstoque?: number;
  custoEstoqueUnitCentavos?: number | null;
}

export function useCotacoes() {
  return useQuery({
    queryKey: [...KEY, 'cotacoes'] as const,
    queryFn: () => api.get<{ items: CotacaoListItem[] }>('/api/v1/compras/cotacoes'),
    enabled: hasToken(),
  });
}
export function useCotacaoFull(id: string | null) {
  return useQuery({
    queryKey: [...KEY, 'cotacao', id] as const,
    queryFn: () => api.get<CotacaoFull>(`/api/v1/compras/cotacoes/${id}`),
    enabled: hasToken() && !!id,
  });
}
export function useFornecedoresPorTipo(tipoItemId: string | null) {
  return useQuery({
    queryKey: [...KEY, 'forn-por-tipo', tipoItemId] as const,
    queryFn: () =>
      api.get<{ items: Fornecedor[] }>(`/api/v1/compras/fornecedores-por-tipo/${tipoItemId}`),
    enabled: hasToken() && !!tipoItemId,
  });
}

// Fase 2 de Compras (15/09/2026): `useCreateCotacao` (cotação vazia, por título) MORREU junto com a
// rota `POST /cotacoes` — a cotação nasce do Quadro (`useCotacaoFromSolicitacao`), nunca de tela vazia.

/**
 * FX-6: cria uma cotação NOVA a partir de uma solicitação (itens já ligados).
 * Idempotente no backend — devolve a cotação existente se já houver uma ligada.
 * Invalida a lista de cotações E o quadro (o card passa a ter `cotacaoId`).
 */
export function useCotacaoFromSolicitacao() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (solicitacaoId: string) =>
      api.post<{ cotacaoId: string; created: boolean }>(
        '/api/v1/compras/cotacoes/from-solicitacao',
        {
          solicitacaoId,
        }
      ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: [...KEY, 'cotacoes'] });
      qc.invalidateQueries({ queryKey: QUADRO_KEY });
    },
  });
}

/** FX-6: adiciona os itens da solicitação a uma cotação EXISTENTE (compra em volume). */
export function useAddSolicitacaoToCotacao() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ cotacaoId, solicitacaoId }: { cotacaoId: string; solicitacaoId: string }) =>
      api.post<{ cotacaoId: string; added: number }>(
        `/api/v1/compras/cotacoes/${cotacaoId}/add-solicitacao`,
        {
          solicitacaoId,
        }
      ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: [...KEY, 'cotacoes'] });
      qc.invalidateQueries({ queryKey: QUADRO_KEY });
    },
  });
}
export function useDeleteCotacao() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.delete<{ ok: true }>(`/api/v1/compras/cotacoes/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: [...KEY, 'cotacoes'] }),
  });
}

/** Mutations de uma cotação aberta. Cada uma devolve o `CotacaoFull` recalculado. */
export function useCotacaoActions(cotacaoId: string) {
  const qc = useQueryClient();
  const setFull = (full: CotacaoFull) => {
    qc.setQueryData([...KEY, 'cotacao', cotacaoId], full);
    qc.invalidateQueries({ queryKey: [...KEY, 'cotacoes'] });
    // SU-2: ações na cotação movem o card no quadro (status canônico) → refresca o quadro.
    qc.invalidateQueries({ queryKey: QUADRO_KEY });
  };
  const base = `/api/v1/compras/cotacoes/${cotacaoId}`;
  const update = useMutation({
    mutationFn: (patch: { titulo?: string; status?: CotacaoStatus }) =>
      api.patch<CotacaoFull>(base, patch),
    onSuccess: setFull,
  });
  // Fase 2: `addItem` (item em texto livre) MORREU com a rota `POST /cotacoes/:id/itens` — o item vem do
  // levantamento (só com código), pelo Quadro (Cotar / Juntar a esta cotação).
  const updateItem = useMutation({
    mutationFn: ({ itemId, patch }: { itemId: string; patch: UpdateCotacaoItemInput }) =>
      api.patch<CotacaoFull>(`${base}/itens/${itemId}`, patch),
    onSuccess: setFull,
  });
  const deleteItem = useMutation({
    mutationFn: (itemId: string) => api.delete<CotacaoFull>(`${base}/itens/${itemId}`),
    onSuccess: setFull,
  });
  const setBloco = useMutation({
    mutationFn: ({
      itemId,
      fornecedorId,
      patch,
    }: {
      itemId: string;
      fornecedorId: string;
      patch: { cotacaoUnitCentavos?: number; descontoPct?: number };
    }) => api.put<CotacaoFull>(`${base}/itens/${itemId}/fornecedores/${fornecedorId}`, patch),
    onSuccess: setFull,
  });
  const removeBloco = useMutation({
    mutationFn: ({ itemId, fornecedorId }: { itemId: string; fornecedorId: string }) =>
      api.delete<CotacaoFull>(`${base}/itens/${itemId}/fornecedores/${fornecedorId}`),
    onSuccess: setFull,
  });
  // FX-36: grava o Nº Pedido/NF na linha do fornecedor (obrigatório na efetivação).
  const setPedidoNf = useMutation({
    mutationFn: ({
      itemId,
      fornecedorId,
      numeroPedidoNf,
    }: {
      itemId: string;
      fornecedorId: string;
      numeroPedidoNf: string | null;
    }) =>
      api.put<CotacaoFull>(`${base}/itens/${itemId}/fornecedores/${fornecedorId}/pedido-nf`, {
        numeroPedidoNf,
      }),
    onSuccess: setFull,
  });
  // D5b — lança o valor comprado do item no realizado do projeto vinculado.
  // CO-6: `status` distingue lançamento direto (flag OFF) de pendente de
  // aprovação do Financeiro (flag ON).
  const lancarRealizado = useMutation({
    mutationFn: (itemId: string) =>
      api.post<{ lancado: LancarRealizadoLancado; cotacao: CotacaoFull }>(
        `${base}/itens/${itemId}/lancar-realizado`,
        {}
      ),
    // FX-35: lançar só LIBERA (aprovado/pendente) — não escreve no realizado.
    onSuccess: (data) => setFull(data.cotacao),
  });  // FX-35: efetivar (Aprovado → Comprado) — AQUI grava o realizado + crédito.
  const efetivar = useMutation({
    mutationFn: (itemId: string) =>
      api.post<{ efetivado: { projetoId: string }; cotacao: CotacaoFull }>(
        `${base}/itens/${itemId}/efetivar`,
        {}
      ),
    onSuccess: (data) => {
      setFull(data.cotacao);
      const pid = data.efetivado.projetoId;
      qc.invalidateQueries({ queryKey: ['projetos', pid] });
    },
  });
  // 🔴 18/09 (decisão 1 do buraco): o Compras APLICA a quantidade do levantamento com um clique (o número sai do servidor na hora).
  const aplicarLevantamento = useMutation({
    mutationFn: (itemId: string) =>
      api.post<{ aplicado: { de: number; para: number }; cotacao: CotacaoFull }>(`${base}/itens/${itemId}/aplicar-levantamento`, {}),
    onSuccess: (data) => setFull(data.cotacao),
  });
  // 🔴 #367: o Compras aceita a divergência (motivo obrigatório) ou desfaz a aceitação. As duas com trilha no servidor.
  const aceitarDivergencia = useMutation({
    mutationFn: ({ itemId, motivo }: { itemId: string; motivo: string }) =>
      api.post<{ aceita: { id: string }; cotacao: CotacaoFull }>(`${base}/itens/${itemId}/aceitar-divergencia`, { motivo }),
    onSuccess: (data) => setFull(data.cotacao),
  });
  const desfazerAceitacao = useMutation({
    mutationFn: (itemId: string) => api.post<{ desfeita: { id: string }; cotacao: CotacaoFull }>(`${base}/itens/${itemId}/desfazer-aceitacao`, {}),
    onSuccess: (data) => setFull(data.cotacao),
  });
  return {
    update,
    updateItem,
    deleteItem,
    setBloco,
    removeBloco,
    setPedidoNf,
    lancarRealizado,
    efetivar,
    aplicarLevantamento,
    aceitarDivergencia,
    desfazerAceitacao,
  };
}

/** FX-35: resultado do lançar-realizado: LIBERADA (aprovado). Não escreve no realizado — isso sai na
 *  efetivação. Fase 2: o 'pendente' morreu com a alçada. */
export type LancarRealizadoLancado = { status: 'aprovado'; valorReais: number; projetoId: string };

// ─── CO-6 — fila de aprovação do Financeiro ───────────────────────────────
// Fase 2 de Compras (decisão do Matheus, 15/09/2026): a ALÇADA DE APROVAÇÃO DO FINANCEIRO MORREU —
// `useAprovacaoConfig`, `useSetAprovacaoConfig`, os re-exports de `@server-types/compras-aprovacao`,
// o tipo `RealizadoPendente` e a ação `reabrir` saíram daqui (zero consumidores depois da tela).

// ─── FX-23 — crédito (livro-razão) por fornecedor ─────────────────────────
export interface CreditoMovimento {
  id: string;
  fornecedorId: string;
  tipo: 'credito' | 'debito';
  valorCentavos: number;
  descricao: string | null;
  origem: string | null;
  cotacaoId: string | null;
  nota: string | null;
  criadoPor: string | null;
  criadoEmIso: string;
}
export interface SaldoFornecedor {
  fornecedorId: string;
  fornecedorNome: string;
  saldoCentavos: number;
  creditosCentavos: number;
  debitosCentavos: number;
}
const CREDITO_KEY = [...KEY, 'credito'] as const;

/** Saldos por fornecedor (indicador). */
export function useCreditoSaldos() {
  return useQuery({
    queryKey: [...CREDITO_KEY, 'saldos'] as const,
    queryFn: () => api.get<{ items: SaldoFornecedor[] }>('/api/v1/compras/credito/saldos'),
    enabled: hasToken(),
  });
}
/** Saldo + histórico de UM fornecedor. */
export function useFornecedorCredito(fornecedorId: string | null) {
  return useQuery({
    queryKey: [...CREDITO_KEY, 'fornecedor', fornecedorId] as const,
    queryFn: () =>
      api.get<{ fornecedorId: string; saldoCentavos: number; movimentos: CreditoMovimento[] }>(
        `/api/v1/compras/fornecedores/${fornecedorId}/credito`
      ),
    enabled: hasToken() && !!fornecedorId,
  });
}
/** FX-28 (B3): ajuste manual (financeiro/admin) — crédito OU débito à mão. */
export function useAjustarCredito() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      fornecedorId,
      tipo,
      valorCentavos,
      cotacaoId,
      nota,
    }: {
      fornecedorId: string;
      tipo: 'credito' | 'debito';
      valorCentavos: number;
      cotacaoId?: string | null;
      nota?: string | null;
    }) =>
      api.post<{ movimento: CreditoMovimento; saldoCentavos: number }>(
        `/api/v1/compras-aprovacao/credito/${fornecedorId}/ajuste`,
        { tipo, valorCentavos, cotacaoId: cotacaoId ?? null, nota: nota ?? null }
      ),
    onSuccess: () => qc.invalidateQueries({ queryKey: CREDITO_KEY }),
  });
}
